// Browser checks provide early feedback; they are not server-side file validation.
export const DOCUMENT_ACCEPT = '.jpg,.jpeg,.png,.pdf';
export const IMAGE_ACCEPT = '.jpg,.jpeg,.png';
export const UPLOAD_HELP = 'JPG/PNG up to 5 MB or PDF up to 10 MB.';
const checked = new WeakMap();

export const validateUpload = async (file, { imagesOnly = false } = {}) => {
  if (!(file instanceof Blob) || !file.name || file.size === 0) throw new Error('Choose a non-empty file.');
  const name = file.name;
  // Allow ordinary dots in names, but reject executable extensions anywhere in the name.
  if (name.length > 200 || /[\\/]/.test(name) || [...name].some(char => char.charCodeAt(0) < 32 || char.charCodeAt(0) === 127) || /^[. ]|[. ]$/.test(name)
    || /\.(php\d*|phtml|phar|exe|com|bat|cmd|ps1|sh|js|html?|svg|aspx?|jsp)(\.|$)/i.test(name)) {
    throw new Error('Use a plain filename without executable extensions or path characters.');
  }
  const extension = name.split('.').pop().toLowerCase();
  const type = { jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', pdf: 'application/pdf' }[extension];
  if (!type || (imagesOnly && extension === 'pdf')) throw new Error(imagesOnly ? 'Choose a JPG or PNG image.' : 'Choose a JPG, PNG or PDF file.');
  const limit = extension === 'pdf' ? 10 : 5;
  if (file.size > limit * 1024 * 1024) throw new Error(`This file exceeds the ${limit} MB limit.`);
  if (file.type && file.type.toLowerCase() !== type) throw new Error('The file type does not match its extension.');
  if (checked.get(file) === type) return;
  const bytes = new Uint8Array(await file.slice(0, 8).arrayBuffer());
  const signature = extension === 'png'
    ? [137, 80, 78, 71, 13, 10, 26, 10]
    : extension === 'pdf' ? [37, 80, 68, 70, 45] : [255, 216, 255];
  if (!signature.every((byte, index) => bytes[index] === byte)) throw new Error('The file contents do not match its extension.');
  if (extension === 'pdf') {
    // A header alone is insufficient even for a basic client-side format check.
    const tail = await file.slice(Math.max(0, file.size - 2048)).text();
    if (!/%%EOF\s*$/.test(tail)) throw new Error('This PDF appears incomplete. Choose another file.');
  } else {
    const url = URL.createObjectURL(file);
    try {
      await new Promise((resolve, reject) => {
        const img = new Image();
        const timer = setTimeout(() => { img.src = ''; reject(new Error('The image could not be opened. Choose another file.')); }, 10000);
        const finish = error => { clearTimeout(timer); img.onload = null; img.onerror = null; error ? reject(error) : resolve(); };
        img.onload = () => finish(!img.naturalWidth || !img.naturalHeight || img.naturalWidth * img.naturalHeight > 40000000
          ? new Error('Choose a valid image no larger than 40 megapixels.') : null);
        img.onerror = () => finish(new Error('The image is damaged or cannot be opened.'));
        img.src = url;
      });
    } finally { URL.revokeObjectURL(url); }
  }
  checked.set(file, type);
};
